<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { format as dateFormat } from 'date-fns'
import { capitalize } from 'lodash'
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useExtendedI18n } from '@/i18n'
import { VSection, VTextField, VTextArea } from '@/modules/shared/components'
import { scrollToError } from '@/modules/shared/utils/form'
import { toNumber, initialMoney, toMoney, changeCurrency, Money } from '@/modules/shared/utils/money'
import { allocateProRata } from '@/modules/shared/utils/allocate-pro-rata'
import { BatchCall } from '../stores/capital-call-store'
import { useCapitalCall } from '../utils/use-capital-call-form'

///////////////////////////////////////////////////////////////////////////////
// Setup
///////////////////////////////////////////////////////////////////////////////

const { n, t } = useExtendedI18n()
const route = useRoute()

///////////////////////////////////////////////////////////////////////////////

const props = defineProps<{
  capital_call: BatchCall
  entity: any
  schedule_call_capital: boolean | undefined
}>()

const emit = defineEmits<{
  (e: 'submit', payload: any): void
}>()

///////////////////////////////////////////////////////////////////////////////

const isNewRecord = computed(() => !props.capital_call.id)
const isPersisted = computed(() => !isNewRecord.value)

const initializeCapitalCall = () => {
  return {
    ...props.capital_call,
    date: props.capital_call.date ? props.capital_call.date.split(/[T ]/)[0] : dateFormat(new Date(), 'yyyy-MM-dd'),
    schedule_date: props.capital_call.schedule_date ? props.capital_call.schedule_date.split(/[T ]/)[0] : null,
    capital_call_transfers: props.capital_call.capital_call_transfers
      .map((transfer) => capitalCallUtil.transformTransfer(transfer as any, props.capital_call))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }
}

///////////////////////////////////////////////////////////////////////////////

// initial state
const whenShouldNotificationBeSent = ref('now') // this is if scheduled_date is present: 'now' -> null, 'later' -> scheduled_date present
const selectedCapitalType = ref('$')
const macros = ref({
  total_capital: null,
})
const capitalCallForm: any = ref(null)

const config = computed(() => ({
  selectedCapitalType: selectedCapitalType.value,
}))
const capitalCallUtil = useCapitalCall(config)

///////////////////////////////////////////////////////////////////////////////
// Investor selection
///////////////////////////////////////////////////////////////////////////////

const toggleAllInvestor = (checked) => {
  if (isNewRecord.value) {
    capitalCallForm.value.capital_call_transfers.forEach((transfer) => {
      transfer.is_active = checked
      if (!checked) capitalCallUtil.resetTransfer(transfer)
    })
    macroUpdateTransfer()
  }
}

const toggleInvestor = (e, transfer) => {
  transfer.is_active = e.target.checked
  if (!transfer.is_active) capitalCallUtil.resetTransfer(transfer)
  macroUpdateTransfer()
}

///////////////////////////////////////////////////////////////////////////////

// FORM DATA
const rules = {
  prior: { required },
  date: { required },
}
const v$ = useVuelidate(rules, capitalCallForm, { $lazy: true })
const updateTransfer = (transfer) => {
  transfer = capitalCallUtil.updateTransfer(transfer)
}

function groupTransfersByInvestor(transfers) {
  return Object.values(
    transfers
      .filter((transfer) => transfer.is_active)
      .reduce((acc, obj) => {
        if (!acc[obj.name]) {
          acc[obj.name] = []
        }
        acc[obj.name].push(obj)
        return acc
      }, {}),
  )
}

const macroUpdateTransfer = () => {
  const grouped = groupTransfersByInvestor(capitalCallForm.value.capital_call_transfers)

  grouped.forEach((group) => {
    if (group.length < 2) {
      group.forEach((transfer) => {
        transfer = capitalCallUtil.updateMacroTransfer(transfer, macros.value)
      })
    } else {
      capitalCallUtil.updateMultipleCommitments(group, macros.value)
    }
  })

  // Apply LRM in $ mode so allocations sum exactly to the entered total
  if (selectedCapitalType.value !== '$' || !macros.value.total_capital) return

  const active = capitalCallForm.value.capital_call_transfers.filter((t) => t.is_active)
  if (active.length === 0) return

  const totalMoney = macros.value.total_capital as Money
  const weights = active.map((t) => t.ownership || 0)
  const allocations = allocateProRata(totalMoney, weights)
  active.forEach((t, i) => {
    t.capital = allocations[i]
    t.commitment_remaining = capitalCallUtil.updateTransfer(t).commitment_remaining
  })
}
const aggregateValues = computed(() => capitalCallUtil.calculateAggregate(capitalCallForm.value.capital_call_transfers))

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) {
    scrollToError(v$, Object.keys(rules))
    return
  }
  let entered_capital_amount = null
  if (selectedCapitalType.value === '$') {
    if (!!macros.value.total_capital) entered_capital_amount = toNumber(macros.value.total_capital)
  } else {
    entered_capital_amount = macros.value.total_capital
  }

  const payload = {
    id: capitalCallForm.value.id,
    investor_set_id: route.params.entity_id,
    capital_amount: toNumber(aggregateValues.value.capital),
    currency: capitalCallForm.value.currency,
    date: capitalCallForm.value.date,
    schedule_date: capitalCallForm.value.schedule_date,
    notes: capitalCallForm.value.notes,
    wiring_instructions: capitalCallForm.value.wiring_instructions,
    prior: `${capitalCallForm.value.prior}`,
    entered_capital_amount: entered_capital_amount,
    selected_capital_type: selectedCapitalType.value,
    capital_call_transfers_attributes: [],
  }

  payload.capital_call_transfers_attributes = capitalCallForm.value.capital_call_transfers
    .filter((transfer) => transfer.is_active)
    .map((transfer) => {
      return {
        id: transfer.id,
        investor_set_commitment_id: transfer.investor_set_commitment_id,
        capital_amount: toNumber(transfer.capital || initialMoney),
        currency: capitalCallForm.value.currency,
        transaction_type: 'capital_call',
      }
    })
  emit('submit', payload) // Emit the 'submit' event with the payload
}
// Pass the submit function back to the parent
defineExpose({
  submit,
})

// TABLE CONFIG
const columns = computed(() => {
  return [
    { key: 'capital', label: 'Capital', visible: true },
    { key: 'commitment_remaining', label: 'Commitment Remaining', disabled: true, visible: true },
  ]
})

const visibleColumns = computed(() => columns.value.filter((column) => column.visible))

onMounted(() => {
  capitalCallForm.value = initializeCapitalCall()
  whenShouldNotificationBeSent.value = props.capital_call.schedule_date ? 'later' : 'now'
  selectedCapitalType.value = props.capital_call.selected_capital_type || '$'
  if (props.schedule_call_capital) {
    capitalCallForm.value.prior = false
    whenShouldNotificationBeSent.value = 'later'
  }

  if (selectedCapitalType.value === '$') {
    macros.value.total_capital = isNewRecord.value
      ? changeCurrency(initialMoney, props.entity.currency)
      : toMoney(props.capital_call.entered_capital_amount, props.capital_call.currency)
  } else {
    macros.value.total_capital = props.capital_call.entered_capital_amount
  }

  watch(
    () => selectedCapitalType.value,
    () => {
      macros.value.total_capital = null
      macroUpdateTransfer()
    },
  )
})
</script>

<template>
  <VSection class="mt-12 space-y-7 px-52" id="investor-allocations" v-if="capitalCallForm">
    <div class="flex gap-3">
      <div class="w-1/2">
        <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
          {{ capitalize(t('shared.general information')) }}
          <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">1/2</span>
        </h3>
      </div>

      <div class="mt-6 w-1/2 space-y-7">
        <VTextField
          v-model="capitalCallForm.date"
          :label="capitalize(capitalCallForm.prior ? t('shared.call date') : t('shared.due date'))"
          type="date"
          property="date"
          :v$="v$"
        />
        <div class="my-12 h-1 bg-gray-100"></div>

        <div>
          <label for="x1" class="block text-sm font-medium leading-6 text-gray-900"
            >Would you like to send an email notification to investors?</label
          >
          <select
            id="x1"
            name="x1"
            v-model="capitalCallForm.prior"
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          >
            <option :value="false">Yes, send notifications to investors</option>
            <option :value="true">No, only record a call</option>
          </select>
        </div>
        <div class="space-y-7" v-if="!capitalCallForm.prior">
          <div>
            <label for="x1" class="block text-sm font-medium leading-6 text-gray-900"
              >When should the email be sent?</label
            >
            <select
              id="x1"
              name="x1"
              v-model="whenShouldNotificationBeSent"
              class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
            >
              <option value="now">Now</option>
              <option value="later">Schedule for later</option>
            </select>
          </div>

          <VTextField
            v-if="whenShouldNotificationBeSent === 'later'"
            v-model="capitalCallForm.schedule_date"
            label="Schedule date"
            type="date"
            property="schedule_date"
            :v$="v$"
          />
          <VTextArea
            v-model="capitalCallForm.notes"
            :label="capitalize(t('shared.note', 0))"
            property="notes"
            :v$="v$"
            description="These notes will be included in the email."
          />
          <VTextArea
            v-model="capitalCallForm.wiring_instructions"
            :label="capitalize(t('shared.wiring instructions'))"
            property="wiring_instructions"
            :v$="v$"
            description="Wiring instructions are sent encrypted. The investor will need to login to access them. "
          />
        </div>

        <div>
          <!-- Step 1: Are you calling capital? -->

          <div class="my-12 h-1 bg-gray-100"></div>

          <!-- Show only if capital is being called -->
          <div class="mt-6">
            <label for="x1" class="block text-sm font-medium leading-6 text-gray-900"
              >Capital to allocate to investors in the grid below</label
            >
            <VTextField
              v-model="macros.total_capital"
              :type="selectedCapitalType === '$' ? 'currency' : 'percent'"
              :v$="v$"
              :useActionsSlot="true"
              inputClass="pl-16"
              :onInput="macroUpdateTransfer"
              :description="selectedCapitalType === '$' ? '' : `Percentage is being applied to total commitment`"
            >
              <template #actions>
                <div class="flex h-full py-1.5">
                  <kbd
                    class="inline-flex items-center rounded-l border border-gray-300 px-2 font-sans text-sm shadow-sm"
                    :class="[
                      selectedCapitalType === '$' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400',
                    ]"
                    @click="selectedCapitalType = '$'"
                    >$</kbd
                  >
                  <kbd
                    class="-ml-px inline-flex items-center rounded-r border border-gray-300 px-2 font-mono text-sm shadow-sm"
                    :class="[
                      selectedCapitalType === '%' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400',
                    ]"
                    @click="selectedCapitalType = '%'"
                    >%</kbd
                  >
                </div>
              </template>
            </VTextField>
          </div>
        </div>
      </div>
    </div>
  </VSection>
  <VSection class="space-y-7 px-52" id="investor-allocations" v-if="capitalCallForm">
    <div class="flex gap-3">
      <div class="w-1/2">
        <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
          {{ capitalize(t('shared.investor allocation')) }}
          <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">2/2</span>
        </h3>
      </div>

      <div class="w-1/2 space-y-7"></div>
    </div>

    <div class="relative rounded-lg border border-gray-200">
      <table class="rounded-lg">
        <thead class="stick">
          <tr>
            <th class="bg-white/50">
              <input
                class="inverse relative block h-4 w-4 rounded border-gray-300 text-white shadow-sm focus:ring-sky-300 focus:ring-opacity-50"
                type="checkbox"
                @change="toggleAllInvestor($event.target['checked'])"
                checked
                :disabled="isPersisted"
              />
            </th>
            <th class="bg-white/50 text-left">Investor</th>
            <th v-for="column in visibleColumns" class="bg-white/50 text-right">
              <div class="flex items-center justify-end space-x-2">
                <div class="inline-block">{{ column.label }}</div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            :class="{ disabledRow: !transfer.is_active }"
            v-for="(transfer, i) in capitalCallForm.capital_call_transfers"
          >
            <td class="px-3" :class="!transfer.is_active ? 'bg-gray-50' : ''">
              <input
                @change="(e) => toggleInvestor(e, transfer)"
                type="checkbox"
                :checked="transfer.is_active"
                class="inverse relative block h-4 w-4 rounded border border-gray-300 text-white shadow-sm focus:ring-sky-300 focus:ring-opacity-50"
                :disabled="isPersisted"
              />
            </td>
            <td class="border-r-[1px] border-gray-300 py-2 pl-3 text-base text-gray-700">
              <div class="font-semibold">{{ transfer.name }}</div>
              <div class="flex gap-2 text-xs">
                <div class="">Commitment:</div>
                <div class="">{{ n(transfer.commitment, 'currency') }}</div>
              </div>
            </td>
            <td v-for="column in visibleColumns">
              <VTextField
                v-model="transfer[column.key]"
                :disabled="!transfer.is_active || !!column.disabled"
                :inputClass="`!border-none !shadow-none !mt-0 text-sm !rounded-none text-right ${
                  transfer.is_active ? 'bg-transparent' : ''
                }`"
                type="currency"
                :onInput="() => updateTransfer(transfer)"
              />
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td></td>
            <td v-for="column in visibleColumns">
              <VTextField
                v-model="aggregateValues[column.key]"
                :disabled="true"
                type="currency"
                inputClass="!border-none !shadow-none !mt-0 text-sm !rounded-none text-right text-gray-800 bg-transparent"
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </VSection>
</template>

<style scoped>
table {
  @apply w-full border-separate border-spacing-0 overflow-x-auto;
}

thead {
  @apply text-[12px] text-xs uppercase text-gray-700;
}

thead.stick th {
  @apply sticky top-[71px] z-50;
}

th {
  @apply min-w-[100px] whitespace-nowrap border-b border-r border-gray-200 px-3 py-3 backdrop-blur sm:min-w-[10px];
}

thead:first-child th:first-child {
  @apply rounded-tl-lg;
}

th:last-child {
  @apply rounded-tr-lg;
}

@media (min-width: 640px) {
  th.fixed-column {
    @apply first:sticky first:left-0 first:z-10 first:border-r;
  }

  td.fixed-column {
    @apply first:sticky first:left-0 first:z-[1] first:min-w-[320px] first:max-w-[480px] first:border-r first:bg-white;
  }

  tfoot td.fixed-column {
    @apply rounded-bl-lg;
  }
}

tbody {
  @apply text-sm text-gray-700;
}

tbody td {
  @apply whitespace-nowrap border-b border-r border-gray-200;
}

th:first-child,
td:first-child {
  @apply border-l-0;
}

th:last-child,
td:last-child {
  @apply border-r-0;
}

tfoot {
  @apply text-sm font-medium uppercase tracking-wide;
}

tfoot td {
  @apply whitespace-nowrap border-r border-gray-200 text-gray-900;
}

table tbody td,
table tfoot td {
  border-top: none !important;
}
</style>
